import React, { useState, useEffect } from 'react';
import { Layout } from "./components/dashboard/layout";
import { Collapse, Box, Typography, Button, Tab, Tabs, Card, CardContent, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Chip, Checkbox, ListItemText, Table, TableHead, TableRow, TableCell, TableBody, Paper, CircularProgress } from '@mui/material';

const DownloadableReports = () => {
  const [students, setStudents] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  const [dupOpen, setDupOpen] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [gradeSummary, setGradeSummary] = useState([]);
  const [overallData, setOverallData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  
    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [gradeFilter, setGradeFilter] = useState([]); // Keep as array consistently
    const [statusFilter, setStatusFilter] = useState('');
    const [deviceFilter, setDeviceFilter] = useState([]);
    const [idCardFilter, setIdCardFilter] = useState(""); // "", "Yes", "No"
    // Add this with your other useState declarations at the top of your component

    const [fromDate, setFromDate] = useState();
    const [toDate, setToDate] = useState();

    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

const fetchStudentData = async () => {
    setIsLoading(true);
    try {
      const studentsResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getStudentAttendanceReports.php?from='+fromDate+'&to='+toDate+'&grades='+gradeFilter.join(",")+'&search='+encodeURIComponent(searchTerm));
      //const summaryResponse = await fetch('https://raptornet.aischennai.org/screening-manager/gate-api/getGradeSummary.php?from='+fromDate+'&to='+toDate);
      
      const studentsData = await studentsResponse.json();
      //const summaryData = await summaryResponse.json();

      if (studentsData.success && Array.isArray(studentsData.data)) {
        setStudents(studentsData.data);
        setFilteredStudents(studentsData.data);
        if (studentsData.duplicates) {
          setDuplicates(studentsData.duplicates);
        }
        setIsLoading(false);
      } else {
        setStudents([]);
        setFilteredStudents([]);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setStudents([]);
      setFilteredStudents([]);
      setGradeSummary([]);
      setOverallData({}); // Empty object, not array
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Student Number', 'Grade', 'First Name', 'Last Name', 'Status', 'Check-In Time', 'Check-Out Time','Check-In Device', 'Check-Out Device', 'ID Card'];
    
    const csvData = filteredStudents.map(student => [
        student.report_date || '',
      student.ps_code || '',
      student.current_grade || '',
      student.first_name || '',
      student.last_name || '',
      getAttendanceStatus(student.checkin_time, student.checkout_time),
      student.checkin_time || '',
      student.checkout_time || '',
      student.checkin_device_name || '',
      student.checkout_device_name || '',
      student.has_id_card || ''
    ]);

    csvData.unshift(headers);
    const csvContent = csvData.map(row => row.join(',')).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = new Date().toISOString().split('T')[0] + '_' + new Date().toTimeString().slice(0, 8).replace(/:/g, '-');
    link.download = `Student_CheckIn-CheckOut_History_${timestamp}.csv`;
    link.click();
  };

  const getAttendanceStatus = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return 'Checked Out';
    if (checkinTime) return 'On Campus';
    return 'Not Checked In';
  };

  const getStatusColor = (checkinTime, checkoutTime) => {
    if (checkinTime && checkoutTime) return '#f44336'; // Red - checked out
    if (checkinTime) return '#4caf50'; // Green - on campus
    return '#ff9800'; // Orange - not checked in
  };

  // Get unique values for filter dropdowns
  const getUniqueGrades = () => {
    const grades = [...new Set(students.map(s => s.current_grade).filter(Boolean))];

    return grades.sort((a, b) => {
      const numA = parseInt(a.replace(/[^-?\d]/g, ""), 10);
      const numB = parseInt(b.replace(/[^-?\d]/g, ""), 10);
      return numA - numB;
    });
  };

  const getUniqueDevices = () => {
    const devices = [...new Set([
      ...students.map(s => s.checkin_device_name).filter(Boolean),
      ...students.map(s => s.checkout_device_name).filter(Boolean)
    ])];
    return devices.sort();
  };

  const getUniqueStatuses = () => {
    return ['On Campus', 'Checked Out', 'Not Checked In'];
  };

  // Get yesterday's date (no current/future dates allowed)
    const getYesterday = () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday.toISOString().split('T')[0];
    };

    // Calculate max date based on from date (1 week limit)
    const getMaxDate = (fromDate) => {
        if (!fromDate) return getYesterday();
        
        const from = new Date(fromDate);
        const maxDate = new Date(from);
        maxDate.setDate(from.getDate() + 6); // 6 days after from date = 1 week total
        
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        // Return the earlier of: (fromDate + 6 days) or yesterday
        return maxDate <= yesterday ? maxDate.toISOString().split('T')[0] : getYesterday();
    };

    // Modified fromDate change handler
    const handleFromDateChange = (e) => {
        const newFromDate = e.target.value;
        setFromDate(newFromDate);
        
        // Reset toDate if it's now invalid
        const maxToDate = getMaxDate(newFromDate);
        if (toDate && toDate > maxToDate) {
            setToDate('');
        }
    };

    // Modified toDate change handler
    const handleToDateChange = (e) => {
        const newToDate = e.target.value;
        const maxToDate = getMaxDate(fromDate);
        
        // Only set if within valid range
        if (newToDate <= maxToDate) {
            setToDate(newToDate);
        }
    };

  // Filter students based on search criteria
  useEffect(() => {
    let filtered = students;

    // Search term filter (searches across multiple fields)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(student => 
        (student.ps_code && student.ps_code.toLowerCase().includes(term)) ||
        (student.first_name && student.first_name.toLowerCase().includes(term)) ||
        (student.last_name && student.last_name.toLowerCase().includes(term)) ||
        (student.current_grade && student.current_grade.toLowerCase().includes(term))
      );
    }

    // Grade filter (multi-select)
    if (gradeFilter.length > 0) {
      filtered = filtered.filter(student => 
        student.current_grade && gradeFilter.includes(student.current_grade)
      );
    }

    // ID Card filter
    if (idCardFilter) {
      filtered = filtered.filter(student => {
        if (idCardFilter === "Yes") return student.has_id_card == "Yes";
        if (idCardFilter === "No") return student.has_id_card == "No" || student.has_id_card === null;
        return true; // fallback
      });
    }

    if (statusFilter && statusFilter !== "All") {
      filtered = filtered.filter(student => {
        const status = getAttendanceStatus(student.checkin_time, student.checkout_time);
        return status === statusFilter;
      });
    }

    // Device filter (checks both check-in and check-out devices)
    if (deviceFilter && deviceFilter.length > 0) {
      filtered = filtered.filter(student =>
        deviceFilter.includes(student.checkin_device_name) ||
        deviceFilter.includes(student.checkout_device_name)
      );
    }

    setFilteredStudents(filtered);
}, [students, searchTerm, gradeFilter, statusFilter, deviceFilter, idCardFilter]);

// Fixed clearFilters function
const clearFilters = () => {
  setSearchTerm('');
  setGradeFilter([]); // Set to empty array, not empty string
  setStatusFilter('');
  setDeviceFilter([]);
  setIdCardFilter('');
};

const getActiveFiltersCount = () => {
  let count = 0;
  if (searchTerm.trim()) count++; // Check for non-empty trimmed string
  if (gradeFilter.length > 0) count++;
  if (statusFilter) count++;
  if (deviceFilter.length > 0) count++;
  if (idCardFilter) count++;
  return count;
};

  const headerCellStyle = {
    padding: '12px 16px',
    borderBottom: '2px solid #ddd',
    fontWeight: '600',
    color: '#333',
    backgroundColor: '#f8f9fa',
    whiteSpace: 'nowrap'
  };

  const cellStyle = {
    padding: '10px 16px',
    borderBottom: '1px solid #eee',
    color: '#444',
    whiteSpace: 'nowrap'
  };

  const summaryCardStyle = {
    textAlign: 'center',
    minHeight: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };

  const duplicatePS = filteredStudents
  .map((s) => s.ps_code)
  .filter((ps, idx, arr) => arr.indexOf(ps) !== idx); // keeps on

  useEffect(() => {
    fetchStudentData();
  }, [selectedDate]);

  return (
    <Layout>
    <Box sx={{ p: 4, maxWidth: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" gutterBottom>
          CheckIn/CheckOut - History
        </Typography>
      </Box>

        <div style={{ fontStyle: 'normal', color: '#777', marginBottom: `10px`, fontSize: `14px`, marginTop: `10px` }}>
          <b>Note: </b>Attendance data is generated based on scan records. There is a potential for up to a few missed scans during entry or exit due to human error.
        </div>

        <Box>
          {<Button onClick={exportToCSV} variant="contained" style={{marginBottom: `10px`,float: `right`,background: `#298cb4`}}>
            Download CSV
          </Button>}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>

            {getActiveFiltersCount() > 0 && (
              <Button 
                variant="outlined" 
                onClick={clearFilters} 
                size="small"
                color="error"
                startIcon={<span>🗑️</span>}
                style={{marginTop: `20px`}}
              >
                Clear All Filters
              </Button>
            )}
          </Box>

          {/* Filter Controls */}
            {/* Row 1: Dates + Grade + Search + Submit */}
                <Box
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                mt={2}
                mb={2}
                >
                {/* From Date */}
                <Box >
                <Typography
                    variant="body2"
                    gutterBottom
                    style={{ fontWeight: "bold" }}
                    >
                    From Date
                    </Typography>
                <TextField
                    size="small"
                    type="date"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                    max: getYesterday(), // No current/future dates
                    }}
                    disabled={isLoading}
                />
                </Box>

                {/* To Date */}
                <Box >
                <Typography
                    variant="body2"
                    gutterBottom
                    style={{ fontWeight: "bold" }}
                    >
                    To Date
                    </Typography>
                <TextField
                    size="small"
                    type="date"
                    value={toDate}
                    onChange={handleToDateChange}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                    min: fromDate, // Can't be before from date
                    max: getMaxDate(fromDate), // Max 1 week from from date or yesterday
                    }}
                    disabled={isLoading || !fromDate}
                />
                </Box>

                {/* Grade */}
                

                {/* Search */}
                <Box minWidth="300px">
                    <Typography
                    variant="body2"
                    gutterBottom
                    style={{ fontWeight: "bold" }}
                    >
                    Search
                    </Typography>
                    <TextField
                    size="small"
                    placeholder="Student Number (or) Name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    fullWidth
                    />
                </Box>

                {/* Grade */}
                <Box>
                <Typography
                    variant="body2"
                    gutterBottom
                    style={{ fontWeight: "bold" }}
                >
                    Grade
                </Typography>
                <Select
                    multiple
                    size="small"
                    value={gradeFilter}
                    onChange={(e) =>
                    setGradeFilter(
                        typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value
                    )
                    }
                    renderValue={(selected) => {
                    if (selected.length === 0) return "All Grades";
                    if (selected.length === 1) return selected[0];
                    return `${selected.length} grades selected`;
                    }}
                    sx={{ minWidth: 250 }}
                >
                    {[
                    "Early years",
                    "Kindergarten",
                    "Grade 1",
                    "Grade 2",
                    "Grade 3",
                    "Grade 4",
                    "Grade 5",
                    "Grade 6",
                    "Grade 7",
                    "Grade 8",
                    "Grade 9",
                    "Grade 10",
                    "Grade 11",
                    "Grade 12"
                    ].map((grade) => (
                    <MenuItem key={grade} value={grade}>
                        <Checkbox checked={gradeFilter.indexOf(grade) > -1} size="small" />
                        <ListItemText primary={grade} />
                    </MenuItem>
                    ))}
                </Select>
                </Box>


                {/* Submit Button */}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={fetchStudentData}
                    disabled={isLoading || !fromDate || !toDate}
                    sx={{ height: "40px", minWidth: "100px", mt: 3 }}
                    startIcon={
                    isLoading ? <CircularProgress size={20} color="inherit" /> : null
                    }
                >
                    {isLoading ? "Loading..." : "Submit"}
                </Button>

                {/* Helper text */}
                {fromDate && (
                    <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 3 }}
                    >
                    Max date range: 1 week
                    </Typography>
                )}
                </Box>

                {/* Row 2: Status + Device + ID Card */}
                
                {filteredStudents.length > 0 && (
                <Box
                    display="flex"
                    alignItems="flex-start"
                    gap={3}
                    flexWrap="wrap"
                    mb={2}
                    style={{ marginTop: `10px` }}
                >
                    {/* Grade */}
                    

                    {/* Status */}
                    <Box minWidth="250px">
                    <Typography
                        variant="body2"
                        gutterBottom
                        style={{ fontWeight: "bold" }}
                    >
                        Status
                    </Typography>
                    <Select
                        size="small"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        renderValue={(selected) => {
                        if (!selected || selected === "All") return "All";
                        return selected;
                        }}
                        sx={{ minWidth: 250 }}
                        fullWidth
                    >
                        <MenuItem value="All">
                        <ListItemText primary="All" />
                        </MenuItem>
                        {getUniqueStatuses().map((status) => (
                        <MenuItem key={status} value={status}>
                            <ListItemText primary={status} />
                        </MenuItem>
                        ))}
                    </Select>
                    </Box>

                    {/* Device */}
                    <Box minWidth="250px">
                    <Typography
                        variant="body2"
                        gutterBottom
                        style={{ fontWeight: "bold" }}
                    >
                        Device
                    </Typography>
                    <Select
                        multiple
                        size="small"
                        value={deviceFilter}
                        onChange={(e) =>
                        setDeviceFilter(
                            typeof e.target.value === "string"
                            ? e.target.value.split(",")
                            : e.target.value
                        )
                        }
                        renderValue={(selected) => {
                        if (!selected || selected.length === 0) return "All Devices";
                        if (selected.length === 1) return selected[0];
                        return `${selected.length} devices selected`;
                        }}
                        sx={{ minWidth: 250 }}
                        fullWidth
                    >
                        <MenuItem value="">
                        <Checkbox checked={deviceFilter.length === 0} />
                        <ListItemText primary="All Devices" />
                        </MenuItem>
                        {getUniqueDevices().map((device) => (
                        <MenuItem key={device} value={device}>
                            <Checkbox checked={deviceFilter.includes(device)} />
                            <ListItemText primary={device} />
                        </MenuItem>
                        ))}
                    </Select>
                    </Box>

                    {/* ID Card */}
                    <Box minWidth="250px">
                    <Typography
                        variant="body2"
                        gutterBottom
                        style={{ fontWeight: "bold" }}
                    >
                        ID Card
                    </Typography>
                    <Select
                        size="small"
                        value={idCardFilter}
                        onChange={(e) => setIdCardFilter(e.target.value)}
                        sx={{ minWidth: 250 }}
                        fullWidth
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                    </Select>
                    </Box>
                </Box>
                )}



          {/*{getActiveFiltersCount() > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">Active filters:</Typography>
              {searchTerm.trim() && (
                <Chip 
                  label={`Search: "${searchTerm}"`} 
                  size="small" 
                  onDelete={() => setSearchTerm('')}
                  color="primary"
                />
              )}
              {gradeFilter.length > 0 && (
                <Chip 
                  label={`Grades: ${gradeFilter.length === 1 ? gradeFilter[0] : `${gradeFilter.length} selected`}`}
                  size="small" 
                  onDelete={() => setGradeFilter([])}
                  color="primary"
                />
              )}
              {statusFilter && (
                <Chip 
                  label={`Status: ${statusFilter}`} 
                  size="small" 
                  onDelete={() => setStatusFilter('')}
                  color="primary"
                />
              )}
              {deviceFilter && (
                <Chip 
                  label={`Device: ${deviceFilter}`} 
                  size="small" 
                  onDelete={() => setDeviceFilter('')}
                  color="primary"
                />
              )}
            </Box>
          )}*/}

          {/*Result Summary*/}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Showing {filteredStudents.length} of {students.length} entries
          </Typography>
          
          {/* Duplicate Section 
            {duplicates.length > 0 && (
                <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDupOpen(!dupOpen)}
                >
                  ⚠️ &nbsp; {duplicates.length} Duplicate
                  {duplicates.length > 1 ? "s" : ""}
                </Button>

                <Collapse in={dupOpen} timeout="auto" unmountOnExit>
                  <Typography
                    variant="h6"
                    color="error"
                    sx={{ mt: 2, mb: 1 }}
                  >
                  Duplicate Entries Detected
                  </Typography>
                  <Table style={{width: `40%`}}>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{fontWeight: `bold`}}>Student Number</TableCell>
                        <TableCell style={{fontWeight: `bold`}}>Student Name</TableCell>
                        <TableCell style={{fontWeight: `bold`}} align="center">Duplicate Count</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {duplicates.map((dup, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{dup.ps_code}</TableCell>
                          <TableCell>
                            {dup.first_name} {dup.last_name}
                          </TableCell>
                          <TableCell align="center">
                            {dup.row_count}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
                </>
            )}*/}

          <Box sx={{ overflowX: 'auto', marginTop: `10px` }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              textAlign: `center`
            }}>
              <thead>
                <tr>
                    <th style={headerCellStyle}>Date</th>
                  <th style={headerCellStyle}>Student Number</th>
                  <th style={headerCellStyle}>Grade</th>
                  <th style={headerCellStyle}>First Name</th>
                  <th style={headerCellStyle}>Last Name</th>
                  <th style={headerCellStyle}>Status</th>
                  <th style={headerCellStyle}>Check-In Time</th>
                  <th style={headerCellStyle}>Check-In Device</th>
                  <th style={headerCellStyle}>Check-Out Time</th>
                  <th style={headerCellStyle}>Check-Out Device</th>
                  <th style={headerCellStyle}>ID Card</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px' }}>
                      {isLoading
                        ? 'Loading student data...'
                        : students.length === 0
                        ? 'No student data available.'
                        : 'No students match the current filters.'}
                    </td>
                  </tr>
                ) : (
                  filteredStudents.map((student, idx) => {
                    const isDuplicate = duplicatePS.includes(student.ps_code);

                    return (
                      <tr
                        key={idx}
                        style={{
                          backgroundColor: idx % 2 === 0
                            ? '#fafafa'
                            : '#ffffff',
                          fontWeight: isDuplicate ? '600' : 'normal',
                        }}
                      >
                        <td style={cellStyle}>{student.report_date || '-'}</td>
                        <td style={cellStyle}>{student.ps_code || '-'}</td>
                        <td style={cellStyle}>{student.current_grade || '-'}</td>
                        <td style={cellStyle}>{student.first_name || '-'}</td>
                        <td style={cellStyle}>{student.last_name || '-'}</td>
                        <td
                          style={{
                            ...cellStyle,
                            color: getStatusColor(student.checkin_time, student.checkout_time),
                            fontWeight: '600',
                          }}
                        >
                          {getAttendanceStatus(student.checkin_time, student.checkout_time)}
                        </td>
                        <td style={cellStyle}>
                          {student.checkin_time
                            ? new Date(student.checkin_time).toLocaleTimeString()
                            : '-'}
                        </td>
                        <td style={cellStyle}>{student.checkin_device_name || '-'}</td>
                        <td style={cellStyle}>
                          {student.checkout_time
                            ? new Date(student.checkout_time).toLocaleTimeString()
                            : '-'}
                        </td>
                        <td style={cellStyle}>{student.checkout_device_name || '-'}</td>
                        <td style={cellStyle}>
                          {student.checkin_device_name ? student.has_id_card : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>
        </Box>
    </Box>
    {isLoading && (
        <Box
            position="fixed"
            top={0}
            left={0}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(0, 0, 0, 0.5)"
            zIndex={9999}
        >
            <CircularProgress size={60} />
        </Box>
    )}
    </Layout>
  );
};

export default DownloadableReports;